import { UseFormReturn } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { getFullName } from '@/lib/typography'
import { AmountInput } from '@/shared/components'
import {
  AnimatedFormLabel,
  FormControl,
  FormField,
  FormItem,
  Input,
} from '@/shared/ui'

import { useWallets } from '../hooks'
import { PaymentLimitType } from '../types'

import { AccountSelect } from './AccountSelect'
import { LimitTypeSelect } from './LimitTypeSelect'
import { UserSelect } from './UserSelect'

type Props = {
  isPending?: boolean
  fullName?: string
  form: UseFormReturn<{
    nickname: string
    walletId: string
    requestId: string
    identityId: string
    limitAmount: string
    limitType: PaymentLimitType
  }>
  onSelectCardholder?: (cardholder: string) => void
}

export const CardFields = ({ fullName, form, onSelectCardholder }: Props) => {
  const intl = useIntl()

  const { wallets } = useWallets()

  const selectedWallet = wallets?.find(
    (wallet) => wallet.id === form.watch('walletId'),
  )

  return (
    <div className="flex flex-col gap-4">
      <FormField
        control={form.control}
        name="identityId"
        render={({ field }) => {
          return (
            <FormItem>
              <UserSelect
                fullName={fullName}
                value={field.value}
                label={intl.formatMessage({
                  id: 'label.cardholder',
                  defaultMessage: 'Cardholder',
                })}
                onChange={({ identityId, teamMember }) => {
                  if (teamMember) {
                    onSelectCardholder?.(getFullName(teamMember))
                  }

                  field.onChange(identityId)
                }}
              />
            </FormItem>
          )
        }}
      />

      <FormField
        control={form.control}
        name="nickname"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Input
                placeholder={intl.formatMessage({
                  id: 'card.field.nickname',
                  defaultMessage: 'Card nickname (i.e. Travel expenses)',
                })}
                {...field}
              />
            </FormControl>
            <AnimatedFormLabel>
              <FormattedMessage
                id="card.field.nickname"
                defaultMessage="Card nickname (i.e. Travel expenses)"
              />
            </AnimatedFormLabel>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="walletId"
        render={({ field }) => {
          return (
            <AccountSelect
              value={field.value}
              showLabel
              label={intl.formatMessage({
                id: 'card.field.sourceAccount',
                defaultMessage: 'Source account',
              })}
              onChange={field.onChange}
            />
          )
        }}
      />

      <FormField
        control={form.control}
        name="limitType"
        render={({ field }) => (
          <FormItem>
            <LimitTypeSelect value={field.value} onChange={field.onChange} />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="limitAmount"
        render={({ field }) => (
          <FormItem>
            <AmountInput
              currency={selectedWallet?.currency}
              placeholder={intl.formatMessage({
                defaultMessage: 'Limit amount',
                id: 'label.limitAmount',
              })}
              {...field}
            />
          </FormItem>
        )}
      />
    </div>
  )
}
